import { endOfDay, format, startOfDay, subDays, subMonths, subWeeks } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TIMEZONE } from 'src/contexts/DateFilterContext';

export function getPlanLeftDays(expiredDate: number) {
    const today = new Date().toISOString();
    const DAY_UNIT_IN_MILLISECONDS = 24 * 3600 * 1000;

    const diffInMilliseconds = new Date(expiredDate).getTime() - new Date(today).getTime()

    const daysLeft = Math.ceil(diffInMilliseconds / DAY_UNIT_IN_MILLISECONDS);
    return daysLeft;
}

export const getValidDate = (dateValue: any) => {
  if (dateValue instanceof Date && dateValue.toString() !== 'Invalid Date') {
    return dateValue;
  }
  return null;
};

export const getReadableDate = (date?: string | Date | null) => {
  if (!date) return;

  return new Date(date).toLocaleDateString('en-NZ', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export type DateRange = {
  dateFrom: string; // ISO string
  dateTo: string; // ISO string
};

export enum INTERVAL_UNIT {
  days = 'days',
  weeks = 'weeks',
  months = 'months',
}

const isoFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";
export const isoFormatWithTz = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx";

export const toLocalIsoString = (date: Date, isStart?: boolean) => {
  if (isStart) {
    return format(startOfDay(date), isoFormat);
  } else {
    return format(endOfDay(date), isoFormat);
  }
};

export const getRelativeDateRange = (selectedDuration: string, selectedInterval: INTERVAL_UNIT): DateRange => {
  const today = new Date();
  let rawDateFrom: Date;
  const duration = Number(selectedDuration);

  switch (selectedInterval) {
    case 'days':
      rawDateFrom = subDays(today, duration);
      break;
    case 'weeks':
      rawDateFrom = subWeeks(today, duration);
      break;
    case 'months':
      rawDateFrom = subMonths(today, duration);
      break;
    default:
      throw new Error('Invalid interval');
  }

  const dateFrom = toLocalIsoString(rawDateFrom, true);
  const dateTo = toLocalIsoString(today);

  return { dateFrom, dateTo };
};

//

const createLocalDate = (date: Date, isStartOfDay?: boolean): Date => {
  return isStartOfDay ? startOfDay(date) : endOfDay(date);
};

const createUTCFromCalendarDate = (
  date: Date,
  isStartOfDay?: boolean
): Date => {
  return new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    isStartOfDay ? 0 : 23,
    isStartOfDay ? 0 : 59,
    isStartOfDay ? 0 : 59,
    isStartOfDay ? 0 : 999
  ));
};

export const formatDateWzTimezone = (date: Date, timezone: TIMEZONE, isStartOfDay?: boolean) => {
  try {
    if (timezone === TIMEZONE.LOCAL) {
      const localDate = createLocalDate(date, isStartOfDay);;
      return format(localDate, isoFormatWithTz);
    }

    const utcDate = createUTCFromCalendarDate(date, isStartOfDay);
    return formatInTimeZone(utcDate, 'UTC', isoFormatWithTz);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

export const getCurrentTimezoneDisplay = (timezone: TIMEZONE) => {
  if (timezone === TIMEZONE.UTC) {
    return 'Based on UTC +00:00';
  }
  
  const now = new Date();
  const formattedDate = format(now, 'xxx'); // Gets offset in the format +02:00
  
  return `Based on UTC ${formattedDate}`;
}

export const getFormattedDate = (dateStr: string | number) => {
  const date = getValidDate(new Date(dateStr));

  if (!date) return '';

  return format(date, 'yyyyMMdd');
}
